/**
 * 規則的結構化複述（AGUI-1113）。
 * 對應 User Story US-R2「以結構化方式複述，而非重複使用者原句」；表單下方即時顯示，規則卡展開時也用同一份。
 *
 * 一個條件一行，用中文連接詞而不是 ∈／AND／THEN 這類符號（使用者裁決）：
 * 第二個條件起以「且」開頭，表示全部都要符合；AI 判斷放在條件之後，講明是「再由 AI 判斷」。
 */
export const EMPTY_RULE = {
  ruleName: '',
  fromAddresses: [],
  fromDomains: [],
  subjectKeywords: [],
  importance: '',
  hasAttachment: null,
  toMe: false,
  aiPrompt: '',
};

/** 複述的最後一行；規則卡上不顯示（每條規則都一樣），靠這個常數辨認 */
export const NOTIFY_LINE = '符合時以 M+ 訊息通知我';

const IMPORTANCE_LABEL = { high: '高', normal: '一般', low: '低' };

/** 附件三態：true＝有附件、false＝沒有附件、null＝不限（回空字串） */
export const attachmentText = (hasAttachment) =>
  hasAttachment === true ? '有附件' : hasAttachment === false ? '沒有附件' : '';

/** 「a」、「a 或 b」、「a、b 或 c」。關鍵字已有「」包住，傳 '或' 不留空白 */
const joinOr = (values, or = ' 或 ') =>
  values.length <= 1 ? values.join('') : `${values.slice(0, -1).join('、')}${or}${values[values.length - 1]}`;

/**
 * 寄件者信箱與寄件網域是<b>同一個條件</b>的兩種寫法，任一符合即可（決議 #8），
 * 所以複述必須合成一行——分成兩行會讓人以為兩者都要符合。
 * 網域補上 `@` 前綴，與後端比對時的語意（`endsWith('@abc.com')`）一致。
 */
const fromLine = (rule) => {
  const addresses = rule.fromAddresses || [];
  const domains = (rule.fromDomains || []).map((d) => (d.startsWith('@') ? d : `@${d}`));
  if (!addresses.length && !domains.length) return null;
  const parts = [];
  if (addresses.length) parts.push(`寄件者是 ${joinOr(addresses)}`);
  if (domains.length) parts.push(`${addresses.length ? '或' : '寄件者'}來自 ${joinOr(domains)}`);
  return parts.join('，');
};

const conditionLines = (rule) => {
  const lines = [];
  const from = fromLine(rule);
  if (from) lines.push(from);
  if (rule.subjectKeywords?.length) {
    lines.push(`主旨包含${joinOr(rule.subjectKeywords.map((k) => `「${k}」`), '或')}`);
  }
  if (rule.importance) lines.push(`重要性為「${IMPORTANCE_LABEL[rule.importance] || rule.importance}」`);
  const attachment = attachmentText(rule.hasAttachment);
  if (attachment) lines.push(attachment);
  if (rule.toMe) lines.push('我在收件者欄');
  return lines;
};

export const hasAnyCondition = (rule) =>
  conditionLines(rule).length > 0 || Boolean(rule.aiPrompt && rule.aiPrompt.trim());

export const summarizeRule = (rule) => {
  const conditions = conditionLines(rule);
  const ai = rule.aiPrompt && rule.aiPrompt.trim();
  if (conditions.length === 0 && !ai) return [];

  const lines = conditions.map((line, i) => (i === 0 ? line : `且${line}`));
  // 寄件者必填（UI、agent_mcp、MyAgent 都擋），有 AI 描述時前面一定有條件
  if (ai) lines.push(`再由 AI 判斷：「${ai}」`);
  lines.push(NOTIFY_LINE);
  return lines;
};
